import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO";
import { Helmet } from "react-helmet-async";
import {
  FaCheckCircle,
  FaLeaf,
  FaBarcode,
  FaDownload,
  FaSun,
  FaBoxOpen,
  FaHeart,
  FaShieldAlt,
  FaAppleAlt,
  FaWarehouse,
  FaThermometerHalf,
  FaCookie,
  FaBreadSlice,
  FaIceCream,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
} from "react-icons/fa";

const DehydratedFruits = () => {
  const { t } = useTranslation();
  const [activeProduct, setActiveProduct] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const products = [
    {
      name: t("dehydratedFruits.products.jackfruit.name"),
      images: [
        "/images/products/dehydrated-jackfruit-pack.webp",
        "/images/products/dehydrated-jackfruit-pieces.webp",
      ],
      image: "🍈",
      color: "from-yellow-400 to-amber-600",
      appearance: t("dehydratedFruits.products.jackfruit.appearance"),
      aroma: t("dehydratedFruits.products.jackfruit.aroma"),
      flavour: t("dehydratedFruits.products.jackfruit.flavour"),
      texture: t("dehydratedFruits.products.jackfruit.texture"),
      ingredient: t("dehydratedFruits.products.jackfruit.ingredient"),
      suggestedUses: [
        t("dehydratedFruits.products.jackfruit.suggestedUses.1"),
        t("dehydratedFruits.products.jackfruit.suggestedUses.2"),
        t("dehydratedFruits.products.jackfruit.suggestedUses.3"),
      ],
      dataSheet: "/images/DataSheet/dehydrated-jackfruit.jpg",
    },
    {
      name: t("dehydratedFruits.products.banana.name"),
      images: [
        "/images/products/dehydrated-banana-pack.webp",
        "/images/products/dehydrated-banana-coins.webp",
      ],
      image: "🍌",
      color: "from-yellow-300 to-yellow-600",
      appearance: t("dehydratedFruits.products.banana.appearance"),
      aroma: t("dehydratedFruits.products.banana.aroma"),
      flavour: t("dehydratedFruits.products.banana.flavour"),
      texture: t("dehydratedFruits.products.banana.texture"),
      ingredient: t("dehydratedFruits.products.banana.ingredient"),
      suggestedUses: [
        t("dehydratedFruits.products.banana.suggestedUses.1"),
        t("dehydratedFruits.products.banana.suggestedUses.2"),
        t("dehydratedFruits.products.banana.suggestedUses.3"),
        t("dehydratedFruits.products.banana.suggestedUses.4"),
      ],
      dataSheet: "/images/DataSheet/dehydrated-banana.jpg",
    },
    {
      name: t("dehydratedFruits.products.mango.name"),
      images: [
        "/images/products/dehydrated-mango-pack.webp",
        "/images/products/dehydrated-mango-strips.webp",
      ],
      image: "🥭",
      color: "from-orange-400 to-red-500",
      appearance: t("dehydratedFruits.products.mango.appearance"),
      aroma: t("dehydratedFruits.products.mango.aroma"),
      flavour: t("dehydratedFruits.products.mango.flavour"),
      texture: t("dehydratedFruits.products.mango.texture"),
      ingredient: t("dehydratedFruits.products.mango.ingredient"),
      suggestedUses: [
        t("dehydratedFruits.products.mango.suggestedUses.1"),
        t("dehydratedFruits.products.mango.suggestedUses.2"),
        t("dehydratedFruits.products.mango.suggestedUses.3"),
        t("dehydratedFruits.products.mango.suggestedUses.4"),
      ],
      dataSheet: "/images/DataSheet/dehydrated-mango.jpg",
    },
    {
      name: t("dehydratedFruits.products.pineapple.name"),
      images: [
        "/images/products/dehydrated-pineapple-pack.webp",
        "/images/products/dehydrated-pineapple-rings.webp",
      ],
      image: "🍍",
      color: "from-yellow-400 to-orange-500",
      appearance: t("dehydratedFruits.products.pineapple.appearance"),
      aroma: t("dehydratedFruits.products.pineapple.aroma"),
      flavour: t("dehydratedFruits.products.pineapple.flavour"),
      texture: t("dehydratedFruits.products.pineapple.texture"),
      ingredient: t("dehydratedFruits.products.pineapple.ingredient"),
      suggestedUses: [
        t("dehydratedFruits.products.pineapple.suggestedUses.1"),
        t("dehydratedFruits.products.pineapple.suggestedUses.2"),
        t("dehydratedFruits.products.pineapple.suggestedUses.3"),
        t("dehydratedFruits.products.pineapple.suggestedUses.4"),
      ],
      dataSheet: "/images/DataSheet/dehydrated-pineapple.jpg",
    },
  ];

  const certifications = [
    {
      name: t("dehydratedFruits.certifications.brc.name"),
      image: "/images/certifications/BRC.webp",
      alt: t("dehydratedFruits.certifications.brc.name"),
      description: t("dehydratedFruits.certifications.brc.description"),
      icon: "🏆",
    },
    {
      name: t("dehydratedFruits.certifications.eu.name"),
      image: "/images/certifications/EU.webp",
      alt: t("dehydratedFruits.certifications.eu.name"),
      description: t("dehydratedFruits.certifications.eu.description"),
      icon: "🇪🇺",
    },
    {
      name: t("dehydratedFruits.certifications.usda.name"),
      image: "/images/certifications/USDA.webp",
      alt: t("dehydratedFruits.certifications.usda.name"),
      description: t("dehydratedFruits.certifications.usda.description"),
      icon: "🇺🇸",
    },
    {
      name: t("dehydratedFruits.certifications.jas.name"),
      image: "/images/certifications/JAS.webp",
      alt: t("dehydratedFruits.certifications.jas.name"),
      description: t("dehydratedFruits.certifications.jas.description"),
      icon: "🇯🇵",
    },
    {
      name: t("dehydratedFruits.certifications.naturland.name"),
      image: "/images/certifications/Naturland.webp",
      alt: t("dehydratedFruits.certifications.naturland.name"),
      description: t("dehydratedFruits.certifications.naturland.description"),
      icon: "🌱",
    },
    {
      name: t("dehydratedFruits.certifications.fairtrade.name"),
      image: "/images/certifications/FairTrade.webp",
      alt: t("dehydratedFruits.certifications.fairtrade.name"),
      description: t("dehydratedFruits.certifications.fairtrade.description"),
      icon: "🤝",
    },
    {
      name: t("dehydratedFruits.certifications.fda.name"),
      image: "/images/certifications/FDA.webp",
      alt: t("dehydratedFruits.certifications.fda.name"),
      description: t("dehydratedFruits.certifications.fda.description"),
      icon: "✅",
    },
  ];

  const commonFeatures = [
    {
      icon: <FaAppleAlt />,
      title: t("dehydratedFruits.commonFeatures.pureFruit.title"),
      desc: t("dehydratedFruits.commonFeatures.pureFruit.desc"),
    },
    {
      icon: <FaSun />,
      title: t("dehydratedFruits.commonFeatures.gentleDehydration.title"),
      desc: t("dehydratedFruits.commonFeatures.gentleDehydration.desc"),
    },
    {
      icon: <FaBoxOpen />,
      title: t("dehydratedFruits.commonFeatures.flexiblePackaging.title"),
      desc: t("dehydratedFruits.commonFeatures.flexiblePackaging.desc"),
    },
    {
      icon: <FaLeaf />,
      title: t("dehydratedFruits.commonFeatures.certifiedOrganic.title"),
      desc: t("dehydratedFruits.commonFeatures.certifiedOrganic.desc"),
    },
  ];

  const idealFor = [
    {
      icon: <FaCookie />,
      text: t("dehydratedFruits.idealFor.snacking"),
    },
    {
      icon: <FaBreadSlice />,
      text: t("dehydratedFruits.idealFor.bakery"),
    },
    {
      icon: <FaIceCream />,
      text: t("dehydratedFruits.idealFor.cereals"),
    },
    {
      icon: <FaAppleAlt />,
      text: t("dehydratedFruits.idealFor.yogurt"),
    },
    {
      icon: <FaHeart />,
      text: t("dehydratedFruits.idealFor.trailMixes"),
    },
    {
      icon: <FaWarehouse />,
      text: t("dehydratedFruits.idealFor.foodService"),
    },
  ];

  const faqs = [
    {
      q: t("dehydratedFruits.faqs.questions.sugars.q"),
      a: t("dehydratedFruits.faqs.questions.sugars.a"),
    },
    {
      q: t("dehydratedFruits.faqs.questions.organic.q"),
      a: t("dehydratedFruits.faqs.questions.organic.a"),
    },
    {
      q: t("dehydratedFruits.faqs.questions.privateLabel.q"),
      a: t("dehydratedFruits.faqs.questions.privateLabel.a"),
    },
    {
      q: t("dehydratedFruits.faqs.questions.shelfLife.q"),
      a: t("dehydratedFruits.faqs.questions.shelfLife.a"),
    },
  ];

  const qualityItems = [
    {
      icon: <FaShieldAlt />,
      title: t("dehydratedFruits.quality.brcProcessing.title"),
      desc: t("dehydratedFruits.quality.brcProcessing.desc"),
    },
    {
      icon: <FaBarcode />,
      title: t("dehydratedFruits.quality.traceability.title"),
      desc: t("dehydratedFruits.quality.traceability.desc"),
    },
    {
      icon: <FaThermometerHalf />,
      title: t("dehydratedFruits.quality.monitoring.title"),
      desc: t("dehydratedFruits.quality.monitoring.desc"),
    },
  ];

  const storageItems = [
    {
      icon: <FaAppleAlt />,
      title: t("dehydratedFruits.storage.readyToEat.title"),
      text: t("dehydratedFruits.storage.readyToEat.text"),
    },
    {
      icon: <FaBoxOpen />,
      title: t("dehydratedFruits.storage.afterOpening.title"),
      text: t("dehydratedFruits.storage.afterOpening.text"),
    },
    {
      icon: <FaWarehouse />,
      title: t("dehydratedFruits.storage.bestQuality.title"),
      text: t("dehydratedFruits.storage.bestQuality.text"),
    },
  ];

  // Reset image index when product changes
  const handleProductChange = (index) => {
    setActiveProduct(index);
    setActiveImageIndex(0);
  };

  // Navigate images
  const nextImage = () => {
    setActiveImageIndex((prev) =>
      prev === products[activeProduct].images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setActiveImageIndex((prev) =>
      prev === 0 ? products[activeProduct].images.length - 1 : prev - 1
    );
  };

  // Download modal component
  const DownloadModal = () => (
    <AnimatePresence>
      {showDownloadModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowDownloadModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-amber-600 to-orange-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-bold mb-1">
                  {t("dehydratedFruits.downloadModal.title")}
                </h3>
                <p className="text-amber-100 text-sm">
                  {t("dehydratedFruits.downloadModal.subtitle")}
                </p>
              </div>
              <button
                onClick={() => setShowDownloadModal(false)}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-all duration-300"
              >
                <FaTimes className="text-2xl" />
              </button>
            </div>

            {/* Product List */}
            <div className="p-6 space-y-3">
              {products.map((product, index) => (
                <motion.a
                  key={index}
                  href={product.dataSheet}
                  download
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-gray-700 dark:to-gray-600 rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group"
                >
                  <div className="flex items-center space-x-4">
                    {/* Product Icon */}
                    <div
                      className={`w-14 h-14 bg-gradient-to-br ${product.color} rounded-xl flex items-center justify-center text-3xl shadow-md`}
                    >
                      {product.image}
                    </div>

                    {/* Product Info */}
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
                        {product.name}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t("dehydratedFruits.downloadModal.productInfo")}
                      </p>
                    </div>
                  </div>

                  {/* Download Icon */}
                  <div className="bg-amber-600 text-white rounded-full p-3 group-hover:bg-amber-700 group-hover:scale-110 transition-all duration-300 shadow-md">
                    <FaDownload className="text-xl" />
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {/* SEO Component */}
      <SEO
        title="Organic Dehydrated Fruits | Premium Dried Jackfruit, Banana, Mango & Pineapple"
        description="100% organic dehydrated fruits from Sri Lanka. No added sugars or preservatives. Premium dried jackfruit, banana coins, mango strips, and pineapple rings. BRC certified, naturally sweet, 12-month shelf life. Perfect for snacking, baking, and trail mixes."
        keywords="organic dehydrated fruits, organic dried fruits, dried jackfruit organic, organic banana chips, dried mango strips, dehydrated pineapple rings, no sugar added dried fruit, BRC certified dried fruits, USDA organic dried fruits, EU organic dehydrated fruits, natural dried fruits Sri Lanka, wholesale dried fruits, private label dried fruits, organic fruit snacks, trail mix ingredients, bakery dried fruits, organic granola toppings, naturally sweet dried fruit, preservative free dried fruits, batch traceable dried fruits"
        image="https://worganaturals.com/images/products/dehydrated-jackfruit-pack.webp"
        url="/products/dehydrated-fruits"
        type="product"
        productSchema={true}
        category="Organic Dehydrated & Dried Fruits"
        brand="Worga Naturals"
        availability="InStock"
        price="9.99"
        currency="USD"
        sku="DF-JACK-1KG"
      />

      {/* Additional Structured Data */}
      <Helmet>
        {/* Product Collection Schema with Variants */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Organic Dehydrated Fruits Collection",
            description:
              "Naturally sweet, simply dried organic fruits from Sri Lanka. Includes jackfruit, banana coins, mango strips, and pineapple rings. Gently dehydrated to preserve flavor and nutrients. 100% pure fruit with no added sugars, colors, or preservatives. BRC certified.",
            brand: {
              "@type": "Brand",
              name: "Worga Naturals",
            },
            manufacturer: {
              "@type": "Organization",
              name: "Worga Naturals (Pvt) Ltd.",
              address: {
                "@type": "PostalAddress",
                streetAddress: "No. 152/G, Meepavita, Udamapitigama",
                addressLocality: "Dompe",
                postalCode: "11680",
                addressCountry: "LK",
              },
            },
            image: [
              "https://worganaturals.com/images/products/dehydrated-jackfruit-pack.webp",
              "https://worganaturals.com/images/products/dehydrated-banana-pack.webp",
              "https://worganaturals.com/images/products/dehydrated-mango-pack.webp",
              "https://worganaturals.com/images/products/dehydrated-pineapple-pack.webp",
            ],
            category: "Organic Dehydrated & Dried Fruits",
            offers: {
              "@type": "AggregateOffer",
              availability: "https://schema.org/InStock",
              priceCurrency: "USD",
              lowPrice: "8.99",
              highPrice: "22.99",
              offerCount: "4",
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.9",
              reviewCount: "312",
            },
            additionalProperty: [
              {
                "@type": "PropertyValue",
                name: "Shelf Life",
                value: "12 months",
              },
              {
                "@type": "PropertyValue",
                name: "Pack Sizes",
                value: "1kg, 2.5kg, Custom",
              },
              {
                "@type": "PropertyValue",
                name: "Certifications",
                value:
                  "BRC Food Safety, EU Organic, USDA Organic, JAS, Naturland, Fairtrade, FDA",
              },
              {
                "@type": "PropertyValue",
                name: "Origin",
                value: "Sri Lanka",
              },
              {
                "@type": "PropertyValue",
                name: "Processing",
                value: "Gentle dehydration, HACCP & GMP certified",
              },
              {
                "@type": "PropertyValue",
                name: "Free From",
                value: "No added sugars, colors, flavoring, preservatives",
              },
              {
                "@type": "PropertyValue",
                name: "Packaging",
                value: "Nylon/LLDPE moisture-barrier bags",
              },
            ],
            hasVariant: [
              {
                "@type": "Product",
                name: "Organic Dehydrated Sweet Jackfruit",
                description:
                  "100% organic dehydrated sweet jackfruit pieces. Yellow color with prominent sweet jackfruit aroma and flavor. Dried and slightly sticky texture. Perfect for standalone snacking, tropical granola, and dessert inclusions.",
                sku: "DF-JACK-1KG",
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Appearance",
                    value: "Yellow colour pieces",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Texture",
                    value: "Dried and slightly sticky",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Ingredient",
                    value: "Organic Sweet Jackfruit 100%",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Moisture Content",
                    value: "Controlled for optimal shelf stability",
                  },
                ],
                offers: {
                  "@type": "Offer",
                  price: "11.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
              {
                "@type": "Product",
                name: "Organic Dehydrated Banana Coins",
                description:
                  "100% organic dehydrated banana coins. Round-shaped slices with golden to brown color. Prominent sweet banana aroma with typical banana flavor. Glossy and dried texture. Ideal for trail mix, cereals, bakery toppings, and kids' snacks.",
                sku: "DF-BAN-1KG",
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Appearance",
                    value: "Round-shaped golden to brown slices",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Texture",
                    value: "Glossy and dried",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Ingredient",
                    value: "Organic Banana 100%",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Cut Style",
                    value: "Coins (round slices)",
                  },
                ],
                offers: {
                  "@type": "Offer",
                  price: "9.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
              {
                "@type": "Product",
                name: "Organic Dehydrated Mango Strips",
                description:
                  "100% organic TJC mango strips. Yellow color with prominent mango aroma and typical mango flavor. Dried texture. Perfect for premium snacking, fruit teas, confectionery inclusions, and cheese boards.",
                sku: "DF-MAN-1KG",
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Appearance",
                    value: "Yellow colour strips",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Texture",
                    value: "Dried",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Ingredient",
                    value: "Organic TJC Mango 100%",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Cut Style",
                    value: "Strips",
                  },
                ],
                offers: {
                  "@type": "Offer",
                  price: "12.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
              {
                "@type": "Product",
                name: "Organic Dehydrated Pineapple Rings & Pieces",
                description:
                  "100% organic dehydrated pineapple available in rings or pieces. Yellow color with prominent pineapple aroma and typical pineapple flavor. Dried texture. Ideal for bakery, chocolate enrobed snacks, trail mixes, and catering.",
                sku: "DF-PINE-1KG",
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Appearance",
                    value: "Yellow colour pieces/rings",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Texture",
                    value: "Dried",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Ingredient",
                    value: "Organic Pineapple 100%",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Cut Style",
                    value: "Rings or Pieces",
                  },
                ],
                offers: {
                  "@type": "Offer",
                  price: "10.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
            ],
          })}
        </script>

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Are there any added sugars or preservatives in Worga Naturals dehydrated fruits?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No—100% fruit, no additives. Our dehydrated fruits contain only pure organic fruit with no added sugars, colors, flavoring, or preservatives. They are naturally sweet from the concentrated fruit sugars.",
                },
              },
              {
                "@type": "Question",
                name: "Are Worga Naturals dehydrated fruits truly organic?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes—all our dehydrated fruits are certified organic by EU Organic, USDA (NOP), JAS, and Naturland. Every batch is fully traceable from farm to package with identity-preserved organic supply chain.",
                },
              },
              {
                "@type": "Question",
                name: "Can I get private label or different cuts?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes—custom sizes, formats (coins, strips, rings, pieces), and branding are available. We offer flexible packaging options from 100g sachets to 2.5kg bulk bags with private label services.",
                },
              },
              {
                "@type": "Question",
                name: "What is the shelf life of dehydrated fruits?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "12 months from date of manufacture under recommended cool, dry storage conditions. Moisture and water activity are carefully monitored to ensure shelf stability. Reseal after opening to maintain texture.",
                },
              },
              {
                "@type": "Question",
                name: "How are the fruits dehydrated?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Each fruit is carefully selected at peak maturity, hygienically processed, and gently dehydrated using controlled temperature and airflow. This preserves natural flavor, aroma, and nutritional attributes without any additives.",
                },
              },
              {
                "@type": "Question",
                name: "What are the best uses for dehydrated fruits?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Our dehydrated fruits are ideal for snacking, bakery applications, cereals/granola, yogurt toppers, trail mixes, food service, fruit teas, confectionery inclusions, and cheese boards. They're ready to eat and versatile for various culinary applications.",
                },
              },
            ],
          })}
        </script>

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://worganaturals.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Products",
                item: "https://worganaturals.com/products",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "Dehydrated Fruits",
                item: "https://worganaturals.com/products/dehydrated-fruits",
              },
            ],
          })}
        </script>

        {/* Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Worga Naturals",
            url: "https://worganaturals.com",
            logo: "https://worganaturals.com/images/logo.png",
            description:
              "Premium organic products from Sri Lanka including dehydrated fruits, spices, canned fruits, coconut water, and natural preserves. BRC certified with full traceability.",
            address: {
              "@type": "PostalAddress",
              streetAddress: "No. 152/G, Meepavita, Udamapitigama",
              addressLocality: "Dompe",
              postalCode: "11680",
              addressCountry: "LK",
            },
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+94-XX-XXXXXXX",
              contactType: "Customer Service",
              availableLanguage: ["en", "si"],
            },
            sameAs: [
              "https://www.facebook.com/worganaturals",
              "https://www.instagram.com/worganaturals",
              "https://www.linkedin.com/company/worganaturals",
            ],
          })}
        </script>

        {/* ItemList Schema for Product Collection */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Organic Dehydrated Fruits Collection",
            description:
              "Premium organic dehydrated fruits from Sri Lanka - naturally sweet with no additives",
            numberOfItems: 4,
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                item: {
                  "@type": "Product",
                  name: "Organic Dehydrated Sweet Jackfruit",
                  url: "https://worganaturals.com/products/dehydrated-fruits#jackfruit",
                  image:
                    "https://worganaturals.com/images/products/dehydrated-jackfruit-pack.webp",
                  offers: {
                    "@type": "Offer",
                    price: "11.99",
                    priceCurrency: "USD",
                  },
                },
              },
              {
                "@type": "ListItem",
                position: 2,
                item: {
                  "@type": "Product",
                  name: "Organic Dehydrated Banana Coins",
                  url: "https://worganaturals.com/products/dehydrated-fruits#banana",
                  image:
                    "https://worganaturals.com/images/products/dehydrated-banana-pack.webp",
                  offers: {
                    "@type": "Offer",
                    price: "9.99",
                    priceCurrency: "USD",
                  },
                },
              },
              {
                "@type": "ListItem",
                position: 3,
                item: {
                  "@type": "Product",
                  name: "Organic Dehydrated Mango Strips",
                  url: "https://worganaturals.com/products/dehydrated-fruits#mango",
                  image:
                    "https://worganaturals.com/images/products/dehydrated-mango-pack.webp",
                  offers: {
                    "@type": "Offer",
                    price: "12.99",
                    priceCurrency: "USD",
                  },
                },
              },
              {
                "@type": "ListItem",
                position: 4,
                item: {
                  "@type": "Product",
                  name: "Organic Dehydrated Pineapple Rings & Pieces",
                  url: "https://worganaturals.com/products/dehydrated-fruits#pineapple",
                  image:
                    "https://worganaturals.com/images/products/dehydrated-pineapple-pack.webp",
                  offers: {
                    "@type": "Offer",
                    price: "10.99",
                    priceCurrency: "USD",
                  },
                },
              },
            ],
          })}
        </script>

        {/* NutritionInformation Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NutritionInformation",
            description:
              "Concentrated natural fruit sugars, fiber, vitamins, and minerals preserved through gentle dehydration",
            servingSize: "30g",
            calories: "90-110 kcal (varies by fruit)",
            sugarContent: "Natural fruit sugars only - no added sugars",
            fiberContent: "High in dietary fiber",
            fatContent: "Naturally low fat",
          })}
        </script>

        {/* Recipe Schema - Trail Mix */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Recipe",
            name: "Tropical Trail Mix with Organic Dehydrated Fruits",
            description:
              "Healthy and delicious trail mix featuring Worga Naturals organic dehydrated fruits",
            image:
              "https://worganaturals.com/images/recipes/tropical-trail-mix.webp",
            recipeIngredient: [
              "1 cup Organic Dehydrated Banana Coins",
              "1 cup Organic Dehydrated Mango Strips",
              "1 cup Organic Dehydrated Pineapple Pieces",
              "1 cup raw almonds",
              "1/2 cup cashews",
              "1/2 cup pumpkin seeds",
              "1/4 cup coconut flakes (optional)",
            ],
            recipeInstructions: [
              {
                "@type": "HowToStep",
                text: "Combine all dehydrated fruits in a large bowl",
              },
              {
                "@type": "HowToStep",
                text: "Add nuts and seeds",
              },
              {
                "@type": "HowToStep",
                text: "Mix well and store in an airtight container",
              },
              {
                "@type": "HowToStep",
                text: "Enjoy as a healthy snack on-the-go",
              },
            ],
            prepTime: "PT5M",
            totalTime: "PT5M",
            recipeYield: "8 servings",
            recipeCategory: "Snack",
            recipeCuisine: "International",
            keywords:
              "trail mix, healthy snacks, organic dried fruits, energy snacks",
            nutrition: {
              "@type": "NutritionInformation",
              calories: "180 calories",
              servingSize: "1/4 cup",
            },
          })}
        </script>

        {/* HowTo Schema - Storage Tips */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to Store Dehydrated Fruits for Maximum Freshness",
            description:
              "Best practices for storing organic dehydrated fruits to maintain quality",
            step: [
              {
                "@type": "HowToStep",
                name: "Store in Cool, Dry Place",
                text: "Keep dehydrated fruits away from direct sunlight and heat sources",
              },
              {
                "@type": "HowToStep",
                name: "Reseal After Opening",
                text: "Always reseal the package after opening to prevent moisture absorption",
              },
              {
                "@type": "HowToStep",
                name: "Use Airtight Container",
                text: "Transfer to an airtight container for extended freshness after opening",
              },
              {
                "@type": "HowToStep",
                name: "Check for Moisture",
                text: "If fruits become sticky or show signs of moisture, consume promptly",
              },
            ],
          })}
        </script>

        {/* Video Schema - Product Overview (if you have video) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: "Worga Naturals Organic Dehydrated Fruits - Production Process",
            description:
              "See how we gently dehydrate organic fruits to preserve natural flavor and nutrients",
            thumbnailUrl:
              "https://worganaturals.com/images/video-thumbnails/dehydrated-fruits-process.webp",
            uploadDate: "2024-01-20",
            duration: "PT3M15S",
            contentUrl:
              "https://worganaturals.com/videos/dehydrated-fruits-process.mp4",
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        {/* Download Modal */}
        <DownloadModal />

        {/* Spacer for navbar */}
        <div className="h-24"></div>

        {/* Hero Section */}
        <section className="container mx-auto px-4 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto text-center"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-block mb-6"
            >
              <span className="px-6 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-full text-sm font-semibold shadow-lg">
                {t("dehydratedFruits.hero.badge")}
              </span>
            </motion.div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
              <span className="text-gray-900 dark:text-white">
                {t("dehydratedFruits.hero.title.part1")}{" "}
              </span>
              <span className="text-amber-600 dark:text-amber-500">
                {t("dehydratedFruits.hero.title.part2")}
              </span>
            </h1>

            <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed max-w-4xl mx-auto mb-8">
              {t("dehydratedFruits.hero.description")}
            </p>

            {/* Traceability Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center space-x-3 bg-white dark:bg-gray-800 rounded-full px-6 py-3 shadow-lg"
            >
              <FaBarcode className="text-amber-600 text-2xl" />
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                {t("dehydratedFruits.hero.traceability")}
              </span>
            </motion.div>
          </motion.div>
        </section>

        {/* Common Features */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("dehydratedFruits.commonFeatures.title")}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {commonFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center hover:shadow-2xl transition-all duration-300"
                >
                  <div className="text-5xl text-amber-600 mb-4 flex justify-center">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {feature.desc}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Ideal For Section */}
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl p-8 text-white shadow-2xl">
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-center">
                {t("dehydratedFruits.idealFor.title")}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {idealFor.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center hover:bg-white/30 transition-all duration-300"
                  >
                    <div className="text-3xl mb-2 flex justify-center">
                      {item.icon}
                    </div>
                    <p className="text-sm font-semibold">{item.text}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </section>

        {/* Product Family */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-7xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("dehydratedFruits.products.title")}
            </h2>

            {/* Product Tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {products.map((product, index) => (
                <button
                  key={index}
                  onClick={() => handleProductChange(index)}
                  className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                    activeProduct === index
                      ? "bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg scale-105"
                      : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:shadow-md"
                  }`}
                >
                  <span className="hidden lg:inline">{product.name}</span>
                  <span className="lg:hidden">
                    {product.image} {product.name.split(" ")[2]}
                  </span>
                </button>
              ))}
            </div>

            {/* Active Product Card */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeProduct}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden"
              >
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Product Image Slider */}
                  <div
                    className={`bg-gradient-to-br ${products[activeProduct].color} flex items-center justify-center p-12 md:p-16 relative overflow-hidden`}
                  >
                    {/* Image Display */}
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeImageIndex}
                        initial={{ opacity: 0, scale: 0.9, x: 100 }}
                        animate={{ opacity: 1, scale: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 0.9, x: -100 }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                        className="w-full h-full flex items-center justify-center"
                      >
                        <img
                          src={products[activeProduct].images[activeImageIndex]}
                          alt={`${products[activeProduct].name} - ${t(
                            "dehydratedFruits.navigation.view"
                          )} ${activeImageIndex + 1}`}
                          className="max-w-full max-h-[500px] object-contain drop-shadow-2xl rounded-lg"
                          onError={(e) => {
                            e.target.style.display = "none";
                            const fallback = document.createElement("div");
                            fallback.className = "text-9xl";
                            fallback.textContent =
                              products[activeProduct].image;
                            e.target.parentElement.appendChild(fallback);
                          }}
                        />
                      </motion.div>
                    </AnimatePresence>

                    {/* Navigation Arrows */}
                    {products[activeProduct].images.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 backdrop-blur-sm"
                          aria-label={t(
                            "dehydratedFruits.navigation.previousImage"
                          )}
                        >
                          <FaChevronLeft className="text-amber-600 text-2xl" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 backdrop-blur-sm"
                          aria-label={t(
                            "dehydratedFruits.navigation.nextImage"
                          )}
                        >
                          <FaChevronRight className="text-amber-600 text-2xl" />
                        </button>
                      </>
                    )}

                    {/* Image Indicators */}
                    {products[activeProduct].images.length > 1 && (
                      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-3 bg-black/30 backdrop-blur-md rounded-full px-4 py-2">
                        {products[activeProduct].images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setActiveImageIndex(index)}
                            className={`transition-all duration-300 rounded-full ${
                              activeImageIndex === index
                                ? "bg-white w-10 h-3"
                                : "bg-white/50 hover:bg-white/75 w-3 h-3"
                            }`}
                            aria-label={t(
                              "dehydratedFruits.navigation.goToImage",
                              { number: index + 1 }
                            )}
                          />
                        ))}
                      </div>
                    )}

                    {/* Image Counter */}
                    <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-semibold">
                      {t("dehydratedFruits.products.labels.imageCounter", {
                        current: activeImageIndex + 1,
                        total: products[activeProduct].images.length,
                      })}
                    </div>

                    {/* Decorative sun rays */}
                    <div className="absolute inset-0 opacity-20 pointer-events-none">
                      <FaSun className="absolute top-4 right-4 text-6xl text-white animate-pulse" />
                    </div>
                  </div>

                  {/* Product Details */}
                  <div className="p-8 md:p-12">
                    <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                      {products[activeProduct].name}
                    </h3>

                    {/* Sensory Profile */}
                    <div className="space-y-4 mb-6">
                      <div>
                        <span className="font-bold text-amber-600">
                          {t("dehydratedFruits.products.labels.appearance")}
                        </span>
                        <span className="text-gray-700 dark:text-gray-300 ml-2">
                          {products[activeProduct].appearance}
                        </span>
                      </div>
                      <div>
                        <span className="font-bold text-amber-600">
                          {t("dehydratedFruits.products.labels.aroma")}
                        </span>
                        <span className="text-gray-700 dark:text-gray-300 ml-2">
                          {products[activeProduct].aroma}
                        </span>
                      </div>
                      <div>
                        <span className="font-bold text-amber-600">
                          {t("dehydratedFruits.products.labels.flavour")}
                        </span>
                        <span className="text-gray-700 dark:text-gray-300 ml-2">
                          {products[activeProduct].flavour}
                        </span>
                      </div>
                      <div>
                        <span className="font-bold text-amber-600">
                          {t("dehydratedFruits.products.labels.texture")}
                        </span>
                        <span className="text-gray-700 dark:text-gray-300 ml-2">
                          {products[activeProduct].texture}
                        </span>
                      </div>
                    </div>

                    {/* Ingredient */}
                    <div className="bg-amber-50 dark:bg-gray-700 rounded-xl p-6 mb-6">
                      <h4 className="font-bold text-amber-600 dark:text-amber-400 mb-2">
                        {t("dehydratedFruits.products.labels.ingredient")}
                      </h4>
                      <p className="text-gray-700 dark:text-gray-300 text-lg">
                        {products[activeProduct].ingredient}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        <strong>
                          {t("dehydratedFruits.products.labels.freeFrom")}
                        </strong>{" "}
                        {t("dehydratedFruits.products.labels.freeFromText")}
                      </p>
                    </div>

                    {/* Suggested Uses */}
                    <div className="mb-6">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                        {t("dehydratedFruits.products.labels.suggestedUses")}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {products[activeProduct].suggestedUses.map((use, i) => (
                          <div
                            key={i}
                            className="flex items-center space-x-2 bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg p-3"
                          >
                            <FaCheckCircle className="text-amber-600 flex-shrink-0" />
                            <span className="text-gray-700 dark:text-gray-300 text-sm">
                              {use}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Common Specs */}
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-bold text-gray-900 dark:text-white">
                            {t("dehydratedFruits.products.labels.origin")}
                          </span>
                          <p className="text-gray-600 dark:text-gray-400">
                            {t("dehydratedFruits.products.labels.originValue")}
                          </p>
                        </div>
                        <div>
                          <span className="font-bold text-gray-900 dark:text-white">
                            {t("dehydratedFruits.products.labels.shelfLife")}
                          </span>
                          <p className="text-gray-600 dark:text-gray-400">
                            {t(
                              "dehydratedFruits.products.labels.shelfLifeValue"
                            )}
                          </p>
                        </div>
                        <div className="col-span-2">
                          <span className="font-bold text-gray-900 dark:text-white">
                            {t("dehydratedFruits.products.labels.packaging")}
                          </span>
                          <p className="text-gray-600 dark:text-gray-400">
                            {t(
                              "dehydratedFruits.products.labels.packagingValue"
                            )}
                          </p>
                        </div>
                        <div className="col-span-2">
                          <span className="font-bold text-gray-900 dark:text-white">
                            {t("dehydratedFruits.products.labels.packSizes")}
                          </span>
                          <p className="text-gray-600 dark:text-gray-400">
                            {t(
                              "dehydratedFruits.products.labels.packSizesValue"
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Process Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 text-white shadow-xl"
            >
              <div className="flex items-start space-x-4">
                <FaSun className="text-5xl flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-2xl font-bold mb-3">
                    {t("dehydratedFruits.process.title")}
                  </h3>
                  <p className="text-lg leading-relaxed">
                    {t("dehydratedFruits.process.description")}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </section>

        {/* Certifications */}
        <section className="bg-white dark:bg-gray-800 py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("dehydratedFruits.certifications.title")}
            </h2>

            <div className="flex flex-wrap justify-center items-center gap-6 max-w-5xl mx-auto">
              {certifications.map((cert, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.1 }}
                  className="group"
                >
                  <div className="flex flex-col items-center space-y-2">
                    <div className="w-16 h-16 flex items-center justify-center bg-orange-50 dark:bg-gray-700 rounded-xl p-2 group-hover:bg-orange-100 dark:group-hover:bg-gray-600 transition-colors">
                      <img
                        src={cert.image}
                        alt={cert.alt}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = "none";
                          const fallback = document.createElement("span");
                          fallback.className = "text-2xl";
                          fallback.textContent = cert.icon;
                          e.target.parentElement.appendChild(fallback);
                        }}
                      />
                    </div>
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 text-center max-w-[80px]">
                      {cert.name}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Quality & Safety */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("dehydratedFruits.quality.title")}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {qualityItems.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg text-center hover:shadow-2xl transition-all duration-300"
                >
                  <div className="text-5xl text-amber-600 mb-4 flex justify-center">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Usage & Storage */}
        <section className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 md:p-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-8 text-center">
                {t("dehydratedFruits.storage.title")}
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {storageItems.map((item, i) => (
                  <div key={i} className="text-center">
                    <div className="text-5xl text-amber-600 mb-4 flex justify-center">
                      {item.icon}
                    </div>
                    <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300">
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="bg-white dark:bg-gray-800 py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
                {t("dehydratedFruits.faqs.title")}
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-amber-50 dark:bg-gray-700 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() =>
                        setOpenFaq(openFaq === index ? null : index)
                      }
                      className="w-full text-left p-6 flex justify-between items-center hover:bg-amber-100 dark:hover:bg-gray-600 transition-all duration-300"
                    >
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white pr-4">
                        {faq.q}
                      </h3>
                      <motion.div
                        animate={{ rotate: openFaq === index ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <FaCheckCircle className="text-amber-600 text-2xl flex-shrink-0" />
                      </motion.div>
                    </button>
                    <motion.div
                      initial={false}
                      animate={{ height: openFaq === index ? "auto" : 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 pb-6 text-gray-700 dark:text-gray-300">
                        {faq.a}
                      </p>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Technical Specs Teaser */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 md:p-12 text-white shadow-2xl"
          >
            <div className="text-center">
              <FaDownload className="text-6xl mb-4 mx-auto" />
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t("dehydratedFruits.technical.title")}
              </h2>
              <p className="text-lg mb-6 opacity-90">
                {t("dehydratedFruits.technical.description")}
              </p>
              <button
                onClick={() => setShowDownloadModal(true)}
                className="bg-white text-indigo-600 px-8 py-3 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:scale-105 active:scale-95 flex items-center space-x-2 mx-auto"
              >
                <FaDownload />
                <span>{t("dehydratedFruits.technical.downloadButton")}</span>
              </button>
            </div>
          </motion.div>
        </section>

        {/* Bottom Spacer */}
        <div className="h-16"></div>
      </div>
    </>
  );
};

export default DehydratedFruits;
